import { IPeriodRange } from "../contracts";
import { apiReadingOff, apiReadingOn, IPurchaseOrderMetric, useStore } from "../store";
import { useApi } from "./api-factory";
import { ModuleNames } from "./modules-names";

interface IAggregatedMetrics {
  [key: string]: IPurchaseOrderMetric;
}

export const useMetricsActions = () => {
  const [_, setState] = useStore();
  const { api } = useApi({ moduleId: ModuleNames.Module1 });

  const sortMetricsByStatus = <T extends IPurchaseOrderMetric>(metrics: T[]): T[] => {
    return (metrics || []).sort((a: T, b: T) => {
      return a.status - b.status;
    });
  };

  const aggregateMetricsByLabel = (metrics: IPurchaseOrderMetric[]): IPurchaseOrderMetric[] => {
    const aggregatedMetrics: IAggregatedMetrics = {};

    metrics.reduce<IAggregatedMetrics>((accumulatedValue, metric) => {
      const label = metric.label;

      if (!accumulatedValue[label])
        accumulatedValue[label] = {
          label: label,
          status: metric.status,
          totalCount: 0
        } as IPurchaseOrderMetric;

      accumulatedValue[label].totalCount += metric.totalCount;

      return accumulatedValue;
    }, aggregatedMetrics);

    return Object.keys(aggregatedMetrics).map(key => aggregatedMetrics[key]);
  };

  const getShipmentDeliveredMetrics = async (range: number, from: string, to: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, shipmentDeliveredMetrics: true }
        }
      }));
      const itemMetric = await api.get("v1/ShipmentMetric/shipment-value", {
        range: range,
        filterFromDate: from,
        filterToDate: to
      });
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          shipmentDeliveredMetrics: itemMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, shipmentDeliveredMetrics: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, shipmentDeliveredMetrics: false }
        }
      }));
    }
  };

  const getItemDeliveryPrecisionMetric = async (from: string, to: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemDeliveryPrecisionMetric: true }
        }
      }));
      const deliveryPrecisionMetric = await api.get(
        "v1/PurchaseOrderMetric/deliveryprecision-items",
        {
          OrderCreationDateFrom: from,
          OrderCreationDateTo: to
        }
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          itemDeliveryPrecisionMetric: deliveryPrecisionMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemDeliveryPrecisionMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemDeliveryPrecisionMetric: false }
        }
      }));
    }
  };

  const getOrderDeliveryPrecisionMetric = async (from: string, to: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderDeliveryPrecisionMetric: true }
        }
      }));
      const deliveryPrecisionMetric = await api.get("v1/PurchaseOrderMetric/deliveryprecision", {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to
      });
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          orderDeliveryPrecisionMetric: deliveryPrecisionMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderDeliveryPrecisionMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderDeliveryPrecisionMetric: false }
        }
      }));
    }
  };

  const getOrderCreationDateBarsMetric = async (from: string, to: string, opCos?: number[]) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderCreationDateBarsMetric: true }
        }
      }));

      const queryParams: Record<string, string | number[]> = {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to
      };

      if (opCos && opCos.length > 0) queryParams.Opcos = opCos;

      const metrics: IPurchaseOrderMetric[] = await api.get(
        "v1/PurchaseOrderMetric",
        queryParams,
        undefined,
        true
      );

      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          orderCreationDateBarsMetric: sortMetricsByStatus(metrics)
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderCreationDateBarsMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderCreationDateBarsMetric: false }
        }
      }));
    }
  };

  const getOrderCreationDateDonutMetric = async (from: string, to: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderCreationDateDonutMetric: true }
        }
      }));

      const metrics: IPurchaseOrderMetric[] = await api.get("v1/PurchaseOrderMetric", {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to
      });

      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          orderCreationDateDonutMetric: sortMetricsByStatus(aggregateMetricsByLabel(metrics))
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderCreationDateDonutMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderCreationDateDonutMetric: false }
        }
      }));
    }
  };

  const getItemCreationDateBarsMetric = async (
    from: string,
    to: string,
    orderNumbers: string[],
    opCos: number[]
  ) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemCreationDateBarsMetric: true }
        }
      }));

      const body: Record<string, string | string[] | number[]> = {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to,
        purchaseOrderNumbers: orderNumbers
      };

      if (opCos && opCos.length > 0) body.Opcos = opCos;

      const itemMetrics: IPurchaseOrderMetric[] = await api.post(
        "v1/PurchaseOrderMetric/item-metrics",
        body
      );

      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          itemCreationDateBarsMetric: sortMetricsByStatus(itemMetrics)
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemCreationDateBarsMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemCreationDateBarsMetric: false }
        }
      }));
    }
  };

  const getItemCreationDateDonutMetric = async (from: string, to: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemCreationDateDonutMetric: true }
        }
      }));

      const metrics: IPurchaseOrderMetric[] = await api.post(
        "v1/PurchaseOrderMetric/item-metrics",
        {
          purchaseOrderItemIds: [],
          OrderCreationDateFrom: from,
          OrderCreationDateTo: to
        }
      );

      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          itemCreationDateDonutMetric: sortMetricsByStatus(aggregateMetricsByLabel(metrics))
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemCreationDateDonutMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemCreationDateDonutMetric: false }
        }
      }));
    }
  };

  const getItemCreationPurchaseOrderNumbers = async (
    from: string,
    to: string,
    opCos?: number[],
    callback?: Function
  ) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: {
            ...old.fetchingData.metrics,
            itemCreationDateBarsAvailablePurchaseOrderNumbers: true
          }
        }
      }));

      const queryParams: Record<string, string | number[]> = {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to
      };

      if (opCos && opCos.length > 0) queryParams.Opcos = opCos;

      const purchaseOrderNumbers: string[] = await api.get(
        "v1/PurchaseOrder/numbers",
        queryParams,
        callback,
        true
      );

      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          itemCreationDateBarsAvailablePurchaseOrderNumbers: purchaseOrderNumbers
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: {
            ...old.fetchingData.metrics,
            itemCreationDateBarsAvailablePurchaseOrderNumbers: false
          }
        }
      }));

      setState(old => ({
        ...apiReadingOff(old, f => f.itemCreationDateBarsAvailablePurchaseOrderNumbers),
        availableMetricFiltersPurchaseOrderNumbers: purchaseOrderNumbers
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: {
            ...old.fetchingData.metrics,
            itemCreationDateBarsAvailablePurchaseOrderNumbers: false
          }
        }
      }));
    }
  };

  const getInternalItemCreationPurchaseOrderNumbers = async (
    from: string,
    to: string,
    callback?: Function
  ) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: {
            ...old.fetchingData.metrics,
            internalItemCreationDateBarsAvailablePurchaseOrderNumbers: true
          }
        }
      }));

      const queryParams: Record<string, string | number[]> = {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to
      };

      const purchaseOrderNumbers: string[] = await api.get(
        "v1/InternalPurchaseOrder/numbers",
        queryParams,
        callback,
        true
      );

      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          internalItemCreationDateBarsAvailablePurchaseOrderNumbers: purchaseOrderNumbers
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: {
            ...old.fetchingData.metrics,
            internalItemCreationDateBarsAvailablePurchaseOrderNumbers: false
          }
        }
      }));

      setState(old => ({
        ...apiReadingOff(old, f => f.internalItemCreationDateBarsAvailablePurchaseOrderNumbers),
        availableMetricFiltersPurchaseOrderNumbers: purchaseOrderNumbers
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: {
            ...old.fetchingData.metrics,
            itemCreationDateBarsAvailablePurchaseOrderNumbers: false
          }
        }
      }));
    }
  };

  const getOrderConfirmedDeliveriesMetric = async () => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderConfirmedDeliveriesMetric: true }
        }
      }));
      const orderConfirmedDeliveriesMetric = await api.get(
        "v1/PurchaseOrderMetric/confirmed-deliveries"
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          orderConfirmedDeliveriesMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderConfirmedDeliveriesMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderConfirmedDeliveriesMetric: false }
        }
      }));
    }
  };

  const getItemConfirmedDeliveriesMetric = async () => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemConfirmedDeliveriesMetric: true }
        }
      }));
      const itemConfirmedDeliveriesMetric = await api.get(
        "v1/PurchaseOrderMetric/confirmed-deliveries-items"
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          itemConfirmedDeliveriesMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemConfirmedDeliveriesMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemConfirmedDeliveriesMetric: false }
        }
      }));
    }
  };

  const getOrderMeetRequestedDateMetric = async () => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderMeetRequestedDateMetric: true }
        }
      }));
      const orderMeetRequestedDateMetric = await api.get(
        "v1/PurchaseOrderMetric/meets-requested-date"
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          orderMeetRequestedDateMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderMeetRequestedDateMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderMeetRequestedDateMetric: false }
        }
      }));
    }
  };

  const getItemMeetRequestedDateMetric = async () => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemMeetRequestedDateMetric: true }
        }
      }));
      const itemMeetRequestedDateMetric = await api.get(
        "v1/PurchaseOrderMetric/meets-requested-date-items"
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          itemMeetRequestedDateMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemMeetRequestedDateMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemMeetRequestedDateMetric: false }
        }
      }));
    }
  };

  const getOrderPlannedDeliveriesMetric = async () => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderPlannedDeliveryDateMetric: true }
        }
      }));
      const orderPlannedDeliveryDateMetric = await api.get(
        "v1/PurchaseOrderMetric/planned-delivery"
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          orderPlannedDeliveryDateMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderPlannedDeliveryDateMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderPlannedDeliveryDateMetric: false }
        }
      }));
    }
  };

  const getItemPlannedDeliveriesMetric = async (itemIds: string[]) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemPlannedDeliveryDateMetric: true }
        }
      }));
      const itemPlannedDeliveryDateMetric = await api.post(
        "v1/PurchaseOrderMetric/planned-delivery-items",
        itemIds
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          itemPlannedDeliveryDateMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemPlannedDeliveryDateMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, itemPlannedDeliveryDateMetric: false }
        }
      }));
    }
  };

  const getOrderAverageLeadTimeMetric = async (range: IPeriodRange) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderAverageLeadTimeMetric: true }
        }
      }));
      const orderAverageLeadTimeMetric = await api.get(
        `v1/PurchaseOrderMetric/average-lead-time/${range.value}`
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          orderAverageLeadTimeMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderAverageLeadTimeMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderAverageLeadTimeMetric: false }
        }
      }));
    }
  };

  const getShipmentCountMetric = async (from: string, to: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, shipmentMetric: true }
        }
      }));
      const shipmentMetric = await api.get("v1/PurchaseOrderMetric/shipment-count", {
        ActualDeliveryFrom: from,
        ActualDeliveryTo: to
      });
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          shipmentMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, shipmentMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, shipmentMetric: false }
        }
      }));
    }
  };

  const getOrderLeadTimeMetric = async (range: IPeriodRange) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderLeadTimeMetric: true }
        }
      }));
      const orderLeadTimeMetric = await api.get(`v1/PurchaseOrderMetric/lead-time/${range.value}`);
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          orderLeadTimeMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderLeadTimeMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, orderLeadTimeMetric: false }
        }
      }));
    }
  };

  const getPlantInventoryMetric = async () => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, quantityByPlantMetric: true }
        }
      }));
      const plantInventoryMetric = await api.get("v1/InventoryMetric/quantity-by-plant");
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          quantityByPlantMetric: plantInventoryMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, quantityByPlantMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, quantityByPlantMetric: false }
        }
      }));
    }
  };

  const getInternalOrderCreationDateBarsMetric = async (from: string, to: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalOrderCreationDateBarsMetric: true }
        }
      }));
      const orderMetric = await api.get("v1/InternalPurchaseOrderMetric", {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to
      });
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          internalOrderCreationDateBarsMetric: sortMetricsByStatus(orderMetric)
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalOrderCreationDateBarsMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalOrderCreationDateBarsMetric: false }
        }
      }));
    }
  };

  const getInternalOrderCreationDateDonutMetric = async (from: string, to: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalOrderCreationDateDonutMetric: true }
        }
      }));
      const orderMetric = await api.get("v1/InternalPurchaseOrderMetric", {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to
      });
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          internalOrderCreationDateDonutMetric: sortMetricsByStatus(orderMetric)
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalOrderCreationDateDonutMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalOrderCreationDateDonutMetric: false }
        }
      }));
    }
  };

  const getInternalItemCreationDateBarsMetric = async (
    from: string,
    to: string,
    orderNumbers: string[]
  ) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalItemCreationDateBarsMetric: true }
        }
      }));

      const body: Record<string, string | string[] | number[]> = {
        OrderCreationDateFrom: from,
        OrderCreationDateTo: to,
        InternalPurchaseOrderNumbers: orderNumbers
      };

      const itemMetrics: IPurchaseOrderMetric[] = await api.post(
        "v1/InternalPurchaseOrderMetric/item-metrics",
        body
      );

      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          internalItemCreationDateBarsMetric: sortMetricsByStatus(itemMetrics)
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalItemCreationDateBarsMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, internalItemCreationDateBarsMetric: false }
        }
      }));
    }
  };

  const getMaterialInventoryMetric = async (materialId: string) => {
    try {
      setState(old => ({
        ...apiReadingOn(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, materialInventoryMetric: true }
        }
      }));
      const materialInventoryMetric = await api.get(
        "v1/InternalPurchaseOrderMetric/inventory-material-nwh",
        { materialId }
      );
      setState(old => ({
        ...apiReadingOff(old),
        metrics: {
          ...old.metrics,
          materialInventoryMetric
        },
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, materialInventoryMetric: false }
        }
      }));
    } catch {
      setState(old => ({
        ...apiReadingOff(old),
        fetchingData: {
          ...old.fetchingData,
          metrics: { ...old.fetchingData.metrics, materialInventoryMetric: false }
        }
      }));
    }
  };

  return {
    getOrderCreationDateBarsMetric,
    getOrderCreationDateDonutMetric,
    getItemCreationDateBarsMetric,
    getItemCreationDateDonutMetric,
    getItemCreationPurchaseOrderNumbers,
    getOrderConfirmedDeliveriesMetric,
    getItemConfirmedDeliveriesMetric,
    getOrderMeetRequestedDateMetric,
    getItemMeetRequestedDateMetric,
    getOrderPlannedDeliveriesMetric,
    getItemPlannedDeliveriesMetric,
    getOrderAverageLeadTimeMetric,
    getShipmentCountMetric,
    getOrderLeadTimeMetric,
    getOrderDeliveryPrecisionMetric,
    getItemDeliveryPrecisionMetric,
    getPlantInventoryMetric,
    getInternalOrderCreationDateBarsMetric,
    getInternalOrderCreationDateDonutMetric,
    getInternalItemCreationDateBarsMetric,
    getInternalItemCreationPurchaseOrderNumbers,
    getMaterialInventoryMetric,
    getShipmentDeliveredMetrics
  };
};
